/**
 * Pure transform: a backend Copilot graph payload -> an ECharts option.
 *
 * Chart type follows the backend ``layout``:
 *   - ``metric``   → horizontal bar (entity sized by a numeric measure)
 *   - ``grouped``  → horizontal bar (category bucket sizes)
 *   - ``bipartite``→ force network (true entity↔entity relationships)
 *
 * Wrong chart = bad UX. Document counts must never render as a spider web.
 */

const FALLBACK_COLORS = [
  "#4B6CB7",
  "#2FA37C",
  "#C2703D",
  "#7A5AA6",
  "#3E8EAD",
  "#B4894A",
  "#8A94A6",
];

const BRAND = "#4B6CB7";
const MAX_BARS = 20;

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

/** Title-case person/role labels (matches table formatter). */
function capitalizeDisplayName(value) {
  const text = String(value || "").trim();
  if (!text || text.includes("@") || text.includes("://")) return text;
  return text
    .split(/\s+/)
    .map((word) =>
      word
        .split(/([-'])/)
        .map((chunk) => {
          if (chunk === "-" || chunk === "'" || !chunk) return chunk;
          return chunk.charAt(0).toUpperCase() + chunk.slice(1).toLowerCase();
        })
        .join("")
    )
    .join(" ");
}

function truncateLabel(label, max = 22) {
  const text = capitalizeDisplayName(label);
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

function isMetricHub(node) {
  return String(node?.category || "").startsWith("metric:");
}

function isCategoryHub(node) {
  return String(node?.category || "").startsWith("category:");
}

function barChartHeight(barCount) {
  // Enough room for each bar + axes; cap so the chat bubble stays usable.
  return Math.min(420, Math.max(220, 48 + barCount * 28));
}

/**
 * Horizontal bar chart for entity + numeric measure (document_count, …).
 * Sorted descending so the largest values read first.
 */
function buildMetricBarOption(graph) {
  const nodes = safeArray(graph?.nodes).filter((n) => n && !isMetricHub(n));
  if (nodes.length < 1) return null;

  const categories = safeArray(graph?.categories);
  const hub = categories.find((c) => String(c?.key || "").startsWith("metric:"));
  const measureLabel = hub?.label || "Count";
  const entityCat = categories.find((c) => !String(c?.key || "").startsWith("metric:"));
  const barColor = entityCat?.color || BRAND;

  const sorted = [...nodes].sort(
    (a, b) => (Number(b?.value) || 0) - (Number(a?.value) || 0)
  );
  const clipped = sorted.slice(0, MAX_BARS);
  // Bottom→top in ECharts category axis; reverse so highest sits at top.
  const display = [...clipped].reverse();

  const labels = display.map((n) => truncateLabel(n.label));
  const values = display.map((n) => Number(n.value) || 0);
  const fullLabels = display.map((n) => capitalizeDisplayName(n.label || ""));

  const overflowNote =
    sorted.length > MAX_BARS
      ? `Showing top ${MAX_BARS} of ${sorted.length}`
      : null;

  return {
    __chartKind: "bar",
    __height: barChartHeight(display.length),
    grid: {
      left: 8,
      right: 36,
      top: overflowNote ? 28 : 16,
      bottom: 8,
      containLabel: true,
    },
    title: overflowNote
      ? {
          text: overflowNote,
          left: 8,
          top: 4,
          textStyle: { fontSize: 11, color: "#94a3b8", fontWeight: 500 },
        }
      : undefined,
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      confine: true,
      backgroundColor: "#ffffff",
      borderColor: "#e2e8f0",
      borderWidth: 1,
      textStyle: { color: "#293F6A", fontSize: 12 },
      formatter: (params) => {
        const p = Array.isArray(params) ? params[0] : params;
        const idx = p?.dataIndex;
        const name = fullLabels[idx] ?? p?.name;
        const val = p?.value ?? 0;
        return `<strong>${name}</strong><div style="color:#64748b;margin-top:2px">${measureLabel}: <b style="color:#293F6A">${val}</b></div>`;
      },
    },
    xAxis: {
      type: "value",
      minInterval: 1,
      axisLabel: { fontSize: 10, color: "#94a3b8" },
      splitLine: { lineStyle: { color: "#f1f5f9" } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: "category",
      data: labels,
      axisLabel: { fontSize: 11, color: "#475569" },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [
      {
        type: "bar",
        data: values,
        barMaxWidth: 18,
        itemStyle: {
          color: barColor,
          borderRadius: [0, 4, 4, 0],
        },
        label: {
          show: true,
          position: "right",
          fontSize: 11,
          color: "#64748b",
          formatter: ({ value }) => String(value),
        },
      },
    ],
  };
}

/**
 * Horizontal bar of category bucket sizes (e.g. participants by status).
 */
function buildGroupedBarOption(graph) {
  const hubs = safeArray(graph?.nodes).filter((n) => isCategoryHub(n));
  if (hubs.length < 1) return null;

  const categories = safeArray(graph?.categories);
  const hubCat = categories.find((c) => String(c?.key || "").startsWith("category:"));
  const measureLabel = hubCat?.label || "Group";
  const barColor = hubCat?.color || BRAND;

  const sorted = [...hubs].sort(
    (a, b) => (Number(b?.value) || 0) - (Number(a?.value) || 0)
  );
  const display = [...sorted].reverse();
  const labels = display.map((n) => truncateLabel(n.label, 18));
  const values = display.map((n) => Number(n.value) || 0);
  const fullLabels = display.map((n) => capitalizeDisplayName(n.label || ""));

  return {
    __chartKind: "bar",
    __height: barChartHeight(display.length),
    grid: {
      left: 8,
      right: 36,
      top: 16,
      bottom: 8,
      containLabel: true,
    },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      confine: true,
      backgroundColor: "#ffffff",
      borderColor: "#e2e8f0",
      borderWidth: 1,
      textStyle: { color: "#293F6A", fontSize: 12 },
      formatter: (params) => {
        const p = Array.isArray(params) ? params[0] : params;
        const idx = p?.dataIndex;
        const name = fullLabels[idx] ?? p?.name;
        const val = p?.value ?? 0;
        return `<strong>${name}</strong><div style="color:#64748b;margin-top:2px">${measureLabel}: <b style="color:#293F6A">${val}</b></div>`;
      },
    },
    xAxis: {
      type: "value",
      minInterval: 1,
      axisLabel: { fontSize: 10, color: "#94a3b8" },
      splitLine: { lineStyle: { color: "#f1f5f9" } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: "category",
      data: labels,
      axisLabel: { fontSize: 11, color: "#475569" },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [
      {
        type: "bar",
        data: values,
        barMaxWidth: 22,
        itemStyle: {
          color: barColor,
          borderRadius: [0, 4, 4, 0],
        },
        label: {
          show: true,
          position: "right",
          fontSize: 11,
          color: "#64748b",
          formatter: ({ value }) => String(value),
        },
      },
    ],
  };
}

/**
 * Force network — only for true bipartite entity relationships.
 */
function buildNetworkOption(graph) {
  const nodesIn = safeArray(graph?.nodes);
  const edgesIn = safeArray(graph?.edges);
  if (nodesIn.length < 2 || edgesIn.length < 1) return null;

  const categoriesIn = safeArray(graph?.categories);
  const catIndex = {};
  const categories = categoriesIn.map((c, i) => {
    catIndex[c?.key] = i;
    return {
      name: c?.label || c?.key || `Group ${i + 1}`,
      itemStyle: { color: c?.color || FALLBACK_COLORS[i % FALLBACK_COLORS.length] },
    };
  });

  const maxValue = nodesIn.reduce(
    (max, n) => Math.max(max, Number(n?.value) || 1),
    1
  );

  const labelById = {};
  const data = nodesIn.map((n) => {
    const id = String(n?.id ?? n?.label ?? "");
    const displayLabel = capitalizeDisplayName(n?.label || id);
    labelById[id] = displayLabel;
    const categoryIdx = catIndex[n?.category];
    const v = Number(n?.value) || 1;
    const ratio = maxValue > 1 ? Math.min(Math.max(v / maxValue, 0), 1) : 0.4;
    return {
      id,
      name: id,
      label: displayLabel,
      value: v,
      category: Number.isInteger(categoryIdx) ? categoryIdx : undefined,
      symbolSize: Math.round(18 + 22 * ratio),
    };
  });

  const links = edgesIn
    .map((e) => ({
      source: String(e?.source ?? ""),
      target: String(e?.target ?? ""),
      value: Number(e?.value) || 1,
    }))
    .filter((e) => e.source && e.target);

  if (!links.length) return null;

  const isLarge = data.length > 40;

  return {
    __chartKind: "network",
    __height: 320,
    tooltip: {
      trigger: "item",
      confine: true,
      backgroundColor: "#ffffff",
      borderColor: "#e2e8f0",
      borderWidth: 1,
      textStyle: { color: "#293F6A", fontSize: 12 },
      formatter: (params) => {
        if (params.dataType === "edge") {
          const s = labelById[params.data.source] || params.data.source;
          const t = labelById[params.data.target] || params.data.target;
          return `${s} &rarr; ${t}`;
        }
        const catName = categories[params.data.category]?.name;
        const catLine = catName
          ? `<div style="color:#64748b;font-size:11px">${catName}</div>`
          : "";
        return `<strong>${params.data.label}</strong>${catLine}`;
      },
    },
    legend: categories.length
      ? [
          {
            data: categories.map((c) => c.name),
            top: 6,
            left: "center",
            itemWidth: 10,
            itemHeight: 10,
            textStyle: { fontSize: 11, color: "#475569" },
          },
        ]
      : undefined,
    animationDuration: 500,
    series: [
      {
        type: "graph",
        layout: "force",
        roam: true,
        draggable: true,
        categories,
        data,
        links,
        force: {
          repulsion: isLarge ? 140 : 260,
          edgeLength: isLarge ? 80 : 130,
          gravity: 0.1,
          friction: 0.65,
        },
        label: {
          show: !isLarge,
          position: "right",
          fontSize: 11,
          color: "#334155",
          formatter: (p) => p.data.label,
        },
        labelLayout: { hideOverlap: true },
        emphasis: {
          focus: "adjacency",
          label: { show: true },
          lineStyle: { width: 3 },
        },
        lineStyle: { color: "source", curveness: 0.08, opacity: 0.5 },
        scaleLimit: { min: 0.4, max: 4 },
      },
    ],
  };
}

/**
 * Build a complete ECharts option, or `null` when the payload cannot be shown.
 *
 * @param {object|null|undefined} graph
 * @returns {object|null}
 */
export function buildGraphOption(graph) {
  if (!graph || typeof graph !== "object") return null;
  const layout = String(graph.layout || "").toLowerCase();

  if (layout === "metric") {
    return buildMetricBarOption(graph);
  }
  if (layout === "grouped") {
    return buildGroupedBarOption(graph);
  }
  // bipartite / unknown relational payloads → network
  return buildNetworkOption(graph);
}
